import { isArray } from '@/utils/is'

export type ItemGroupType = {
  conditions: []
  interiorCondition: string
}

export type ItemConditions = {
  rightColumnSerial: string
}

export type AssociationPropsType = {
  isTxt?: boolean
  formData?: Recordable
  positions: Recordable[]
  data: Recordable
}

// 获取关联已有数据字段涉及的组件code和序列号数组
export const getAsoCodeSerialArr = (positions: Recordable[]): Recordable[] =>
  positions.reduce((pre: Recordable[], cur: Recordable) => {
    const formatItem =
      cur?.modules?.[0]?.componentList?.map((item: { fieldCode: string; fieldSerial: string }) => ({
        fieldCode: item.fieldCode,
        fieldSerial: item.fieldSerial
      })) || []

    return (pre = pre.concat(formatItem))
  }, [])

// 获取关联已有数据字段涉及的组件code数组
export const getAsoCodeArr = ({ positions, data }: AssociationPropsType) => {
  // 当前所有过滤条件组数据
  const conditionList = data?.defaultValueConfigInfo?.conditionInfo?.conditionList || []

  const asoCodeSerialArr = getAsoCodeSerialArr(positions)

  const asoSerialArr: string[] = []
  conditionList.forEach((item: ItemGroupType) => {
    item.conditions.forEach((item2: ItemConditions) => {
      asoSerialArr.push(item2.rightColumnSerial)
    })
  })

  const asoCodeArr = asoCodeSerialArr
    .filter(item => asoSerialArr.includes(item.fieldSerial))
    .map(item => item.fieldCode)

  return asoCodeArr
}

// 判断关联已有数据字段条件涉及的表单字段，新旧数据是否相等
export const newOldValIsChange = (
  { formData, positions, data }: AssociationPropsType,
  oldFormData: Recordable
) => {
  const asoCodeArr = getAsoCodeArr({ positions, data })
  return asoCodeArr.some(i => formData?.[i] !== oldFormData?.[i])
}

/**
 * @description 关联已有数据字段回显逻辑处理
 * @param {Object} data 当前字段相关数据
 * @param {Object} positions 当前表单位置、组件数据
 * @param {Object} formData 当前表单值数据
 * @param {Object} oldFormData 当前表单旧值数据
 * @param {Function} callBack 逻辑或与校验通过后的回显值获取函数
 *
 * 可调用回显值获取函数进行字段回显的条件：
 *   条件1---该字段无数据过滤条件
 *   条件2---该字段存在数据过滤条件，但是涉及条件组、子条件字段，逻辑或与校验需要"有值"
 */
export const useDealAssociatedData = (
  { isTxt, data, positions, formData }: AssociationPropsType,
  oldFormData: Recordable,
  callBack: Function
) => {
  const _hasValue = (val: Recordable) => (isArray(val) ? val.length : val)
  const _feildValue = _hasValue(formData?.[data?.fieldCode])
  const _oldFieldValue = _hasValue(oldFormData?.[data?.fieldCode])
  const _doCallBack = async () => {
    const res = await callBack()
    // 关联已有数据---子表更新问题，配合后端传参处理
    const defaultQuoteDataId =
      res?.list?.find((i: { value: any }) => i.value === res?.value)?.defaultQuoteDataId ||
      res?.list?.[0]?.defaultQuoteDataId
    data.renderConfig.defaultQuoteDataId = defaultQuoteDataId || ''
    data.renderConfig.defaultQuoteValue = res?.value || ''
  }

  // 【不进行关联已有数据回显处理】当前字段组件状态为文本(详情)
  if (isTxt) return

  // 【不进行关联已有数据回显处理】若当前字段新值为空、旧值有值，视为清空操作
  if (!_feildValue && _oldFieldValue) return

  // 【不进行关联已有数据回显处理】若当前字段有回显值或者字段的数据类型不为1
  if (data.defaultMark !== 1) return // 有值的情况下，可以切换覆盖(产品童鹏飞)

  // 【不进行关联已有数据回显处理】当前字段的过滤条件涉及的字段值未发生改变
  if (!newOldValIsChange({ formData, positions, data }, oldFormData)) return

  // 当前所有过滤条件组数据
  const conditionList = data?.defaultValueConfigInfo?.conditionInfo?.conditionList || []

  // 无过滤条件，则直接调用回显值获取函数进行字段回显
  if (!conditionList.length) {
    _doCallBack()
    return
  }

  try {
    // 当前表单中涉及字段的组件code和序列号数组
    const asoCodeSerialArr = getAsoCodeSerialArr(positions)

    /**
     * @description 校验字段过滤条件组中，某个子条件组合是否通过
     * @param {Array} conditions 子条件组数据
     * @param {String} interiorCondition 子条件组内部逻辑关系
     */
    const validItemGroup = (conditions: [], interiorCondition: string) => {
      // 子条件组值标志数组
      const itemFlagArr: number[] = []

      conditions.forEach((item: { rightColumnSerial: string }) => {
        // 找到该字段在表单中对应的数据信息
        const ele = asoCodeSerialArr.find(i => i.fieldSerial === item.rightColumnSerial)
        // 根据表单数据中该字段是否有值向数组push值标志
        if (ele) {
          itemFlagArr.push(formData?.[ele.fieldCode] ? 1 : 0)
        }
      })

      // 组内"且"：则全部值标志都要为1
      // 组内"或"：则至少有一个值标志为1
      return interiorCondition === 'AND'
        ? itemFlagArr.reduce((pre, cur) => pre * cur, 1) > 0
        : itemFlagArr.reduce((pre, cur) => pre + cur, 0) >= 1
    }

    // 对所有条件组进行条件校验
    const validGroup = () => {
      // 条件组值标志数组
      const flagArr: number[] = []
      // 第一个条件组的逻辑关系值
      const exteriorCondition = conditionList[0] && conditionList[0].exteriorCondition

      // 所有子条件组进行校验
      conditionList.forEach((itemGroup: ItemGroupType) => {
        const { interiorCondition, conditions } = itemGroup
        flagArr.push(validItemGroup(conditions, interiorCondition) ? 1 : 0)
      })

      return exteriorCondition === 'AND'
        ? flagArr.reduce((pre, cur) => pre * cur, 1) > 0
        : flagArr.reduce((pre, cur) => pre + cur, 0) >= 1
    }

    if (validGroup()) {
      _doCallBack()
    }
  } catch (error) {
    _doCallBack()
  }
}
